import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Lock } from "lucide-react";
import { memo, useEffect } from "react";
import { useForm } from "react-hook-form";
import z from "zod";

import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "../ui";

import { useChangeUserPassword } from "@/common/api";
import { useAuth } from "@/common/hooks";
import { useToast } from "@/common/hooks/useToast";
import { PASSWORD_ALLOWED_CHARS_REGEX } from "@/common/model";

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Текущий пароль обязателен"),
    newPassword: z
      .string()
      .min(1, "Новый пароль обязателен")
      .regex(
        PASSWORD_ALLOWED_CHARS_REGEX,
        "Пароль может содержать только латинские буквы, цифры и специальные символы",
      )
      .min(8, "Минимальная длина пароля - 8 символов")
      .max(64, "Максимальная длина пароля - 64 символов")
      .refine(
        (password) => /[A-Z]/.test(password),
        "Пароль должен содержать латиницу верхнего регистра (A–Z)",
      )
      .refine(
        (password) => /[a-z]/.test(password),
        "Пароль должен содержать латиницу нижнего регистра (a–z)",
      )
      .refine((password) => /[0-9]/.test(password), "Пароль должен содержать цифры (0–9)")
      .refine(
        (password) => /[@$&!%*?^_+\-=\[\]{}()|\\:";'<>.,\/#~`]/.test(password),
        "Пароль должен содержать специальные символы (@, $, &, и др.)",
      ),
    confirmPassword: z.string().min(1, "Подтвердите новый пароль"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Пароли не совпадают",
    path: ["confirmPassword"],
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: "Нельзя использовать старый пароль",
    path: ["newPassword"],
  });

type ChangePasswordData = z.infer<typeof changePasswordSchema>;

export interface ChangePasswordDialogProps {
  isOpen: boolean;
  setIsOpen?: (value: boolean) => void;
  hasButton?: boolean;
  withCurrentPassword?: boolean;
  onSave?: () => void;
  onClose?: () => void;
}

export const ChangePasswordDialog = memo<ChangePasswordDialogProps>(
  ({ isOpen, setIsOpen, hasButton, withCurrentPassword, onSave, onClose }) => {
    const { toast } = useToast();
    const {
      auth: { id },
    } = useAuth();

    const passwordForm = useForm<ChangePasswordData>({
      resolver: zodResolver(changePasswordSchema),
      mode: "onChange",
      defaultValues: {
        currentPassword: withCurrentPassword ? "" : "currentPassword",
        newPassword: "",
        confirmPassword: "",
      },
    });

    const { isPending, mutateAsync } = useChangeUserPassword();

    const onPasswordSubmit = (data: ChangePasswordData) => {
      mutateAsync({
        data: {
          id: id!,
          passwordNewOne: data.newPassword,
          passwordNewTwo: data.confirmPassword,
          passwordOld: withCurrentPassword ? data.currentPassword : undefined,
        },
      })
        .then(() => {
          toast({
            title: "Пароль изменен",
            description: "Ваш пароль успешно изменен",
          });
          onSave?.();
        })
        .catch((error: unknown) => {
          toast({
            variant: "destructive",
            title: "Ошибка",
            description: error instanceof Error ? error.message : "Не удалось изменить пароль",
          });
        });
    };

    useEffect(() => {
      if (isOpen) {
        passwordForm.reset();
      }
    }, [isOpen]);

    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        {hasButton && (
          <DialogTrigger asChild>
            <Button variant="outline" className="md:w-[207px] w-full">
              <Lock className="w-4 h-4 mr-2" />
              Сменить пароль
            </Button>
          </DialogTrigger>
        )}

        <DialogContent className="md:max-w-[30rem] max-w-[22rem]" withoutCross={!hasButton}>
          <DialogHeader className="text-left">
            <DialogTitle>Смена пароля</DialogTitle>
          </DialogHeader>

          <Form {...passwordForm}>
            <form
              onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
              className="md:space-y-5 space-y-4"
              autoComplete="off"
              noValidate
            >
              <FormField
                control={passwordForm.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem hidden={!withCurrentPassword}>
                    {withCurrentPassword && <FormLabel>Текущий пароль</FormLabel>}
                    <FormControl>
                      <Input
                        type={withCurrentPassword ? "password" : "hidden"}
                        placeholder="Введите текущий пароль"
                        {...field}
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={passwordForm.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Новый пароль</FormLabel>
                    <FormControl>
                      <Input
                        title="Пароль может содержать только латинские буквы, цифры и специальные символы"
                        type="password"
                        placeholder="Введите новый пароль"
                        {...field}
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={passwordForm.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Подтвердите новый пароль</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Подтвердите новый пароль"
                        {...field}
                        autoComplete="new-password"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex md:flex-row flex-col md:justify-end justify-center md:space-x-3 space-x-0 md:space-y-0 space-y-3">
                <Button type="button" variant="outline" onClick={onClose}>
                  Отмена
                </Button>
                <Button type="submit" className="btn-primary" disabled={isPending}>
                  {isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Изменение...
                    </>
                  ) : (
                    "Изменить пароль"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  },
);
